/**
 * Spec 218 PR2 — Fiscal profile UI (`/settings/fiscal`).
 *
 * Mobile reference: `mobile/src/screens/FiscalProfileScreen.tsx`.
 * Web port follows the same shape: a single form with the seven
 * `FiscalProfileInput` fields, Spanish labels, NIF validation via
 * the existing `validateTaxId`, and a save button that POSTs to
 * the relay through the `saveFiscalProfile` HTTP helper.
 *
 * Read flow:
 *   - On mount, GET `/invoices/fiscal-profile`. 404 → empty form
 *     (first-time creation). 2xx → pre-fill all fields.
 *   - Subscribe to relay's company-wide invoice updates is not
 *     necessary; the fiscal profile is per-company and changes
 *     rarely. A manual reload picks up admin edits from another
 *     device.
 *
 * Save flow:
 *   - Synchronous client-side validation (required + NIF shape)
 *     blocks the submit button when any field is invalid.
 *   - On click, POST the input. Loading + error states are
 *     surfaced inline; success returns to /settings.
 *
 * Gating:
 *   - The route is admin-only (`creator` / `admin`). A worker
 *     who navigates to /settings/fiscal lands on a "no permission"
 *     empty-state.
 *   - The route is also feature-gated by the relay's GET /features
 *     `verifacti` flag. When OFF, the route renders an
 *     informational notice instead of the form.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, FileText, Save, ShieldCheck } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import {
  getFiscalProfile,
  isVerifactiEnabled,
  saveFiscalProfile,
  type FiscalProfileInput,
  type InvoicingCtx,
} from '../sync/invoicing';
import type { FiscalProfile } from '@cuadrilla/types/invoice';
import type { WebIdentity } from '../store/keys';
import { validateTaxId } from '../utils/validators';

import './SettingsFiscal.css';

interface SettingsFiscalProps {
  identity: WebIdentity;
}

const EMPTY_INPUT: FiscalProfileInput = {
  nif: '',
  legalName: '',
  address: '',
  postalCode: '',
  city: '',
  province: '',
  iban: '',
};

interface FieldErrors {
  nif?: string;
  legalName?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  province?: string;
  iban?: string;
}

function validate(input: FiscalProfileInput): FieldErrors {
  const errors: FieldErrors = {};
  // NIF: required + structural check via shared validator.
  if (!input.nif.trim()) errors.nif = 'NIF requerido.';
  else {
    const taxErr = validateTaxId(input.nif);
    if (taxErr) errors.nif = taxErr;
  }
  if (!input.legalName.trim()) errors.legalName = 'Razón social requerida.';
  if (!input.address.trim()) errors.address = 'Dirección requerida.';
  if (!input.postalCode.trim()) errors.postalCode = 'CP requerido.';
  else if (!/^\d{5}$/.test(input.postalCode.trim())) errors.postalCode = 'CP de 5 dígitos.';
  if (!input.city.trim()) errors.city = 'Localidad requerida.';
  if (!input.province.trim()) errors.province = 'Provincia requerida.';
  // IBAN is optional; if present, structural check (24 chars, ES prefix common
  // but accept any starting with two letters for non-Spanish account import).
  if (input.iban && input.iban.trim()) {
    const sanitized = input.iban.trim().replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(sanitized)) {
      errors.iban = 'IBAN no válido. Formato esperado: ES + 22 cifras.';
    }
  }
  return errors;
}

function buildCtx(identity: WebIdentity): InvoicingCtx {
  return {
    companyToken: identity.companyToken,
  };
}

export function SettingsFiscal({ identity }: SettingsFiscalProps) {
  const navigate = useNavigate();
  const isAdmin = identity.role === 'creator' || identity.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [existing, setExisting] = useState<FiscalProfile | null>(null);
  const [input, setInput] = useState<FiscalProfileInput>(EMPTY_INPUT);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      const ctx = buildCtx(identity);
      try {
        const featureOn = await isVerifactiEnabled(ctx);
        if (cancelled) return;
        setEnabled(featureOn);
        if (featureOn) {
          const profile = await getFiscalProfile(ctx);
          if (cancelled) return;
          setExisting(profile);
          if (profile) {
            setInput({
              nif: profile.nif,
              legalName: profile.legalName,
              address: profile.address,
              postalCode: profile.postalCode,
              city: profile.city,
              province: profile.province,
              iban: profile.iban ?? '',
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setSubmitError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [identity, isAdmin]);

  const errors = validate(input);
  const hasErrors = Object.keys(errors).length > 0;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (hasErrors || submitBusy) return;
    setSubmitBusy(true);
    setSubmitError(null);
    try {
      // Trim trailing whitespace on every text field — reduces server
      // round-trips that fail validation on the AEAT side.
      await saveFiscalProfile(buildCtx(identity), {
        nif: input.nif.trim().toUpperCase(),
        legalName: input.legalName.trim(),
        address: input.address.trim(),
        postalCode: input.postalCode.trim(),
        city: input.city.trim(),
        province: input.province.trim(),
        iban: input.iban?.trim() ? input.iban.trim().replace(/\s+/g, '').toUpperCase() : undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitBusy(false);
    }
  }

  function update<K extends keyof FiscalProfileInput>(key: K, value: FiscalProfileInput[K]) {
    setInput((prev) => ({ ...prev, [key]: value }));
  }

  // ── Gates ─────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <main id="main-content" className="sf-screen" data-testid="settings-fiscal-screen">
        <Link to="/settings" className="sf-back" data-testid="settings-fiscal-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Ajustes
        </Link>
        <EmptyState
          testId="settings-fiscal-no-permission"
          variant="noResults"
          title="Sin permisos"
          body={
            <>
              Solo el propietario o un administrador puede editar el perfil
              fiscal del equipo.
            </>
          }
        />
      </main>
    );
  }

  if (loading) {
    return (
      <main id="main-content" className="sf-screen" data-testid="settings-fiscal-screen">
        <SkeletonList count={4} testId="settings-fiscal-loading" />
      </main>
    );
  }

  if (enabled === false) {
    // Feature flag off — bounce back to the main settings panel
    // rather than render a teaser. The route itself is also no longer
    // mounted in the nav (App.tsx redirects /settings/fiscal to
    // /settings/identidad), so this branch only fires if a stale
    // bookmark mounts the component directly during a soft-rollout.
    return <Navigate to="/settings/identidad" replace />;
  }

  return (
    <main id="main-content" className="sf-screen" data-testid="settings-fiscal-screen">
      <header className="sf-header">
        <Link to="/settings" className="sf-back" data-testid="settings-fiscal-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Ajustes
        </Link>
        <div className="sf-header-title">
          <FileText size={24} strokeWidth={2} aria-hidden="true" />
          <h1 className="sf-h1">Datos fiscales</h1>
        </div>
        <p className="sf-header-subtitle">
          {existing
            ? 'Edita los datos que aparecerán en cada factura emitida.'
            : 'Antes de emitir tu primera factura, completa los datos fiscales del equipo.'}
        </p>
      </header>

      <form className="sf-form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="sf-row">
          <label className="sf-field" htmlFor="sf-nif">
            <span className="sf-label">NIF / CIF *</span>
            <input
              id="sf-nif"
              type="text"
              className="sf-input"
              value={input.nif}
              onChange={(e) => update('nif', e.target.value)}
              placeholder="B12345678"
              autoComplete="off"
              spellCheck={false}
              maxLength={12}
              data-testid="settings-fiscal-nif"
              aria-invalid={Boolean(errors.nif)}
            />
            {errors.nif && <span className="sf-error" data-testid="settings-fiscal-nif-error">{errors.nif}</span>}
          </label>

          <label className="sf-field" htmlFor="sf-legalName">
            <span className="sf-label">Razón social *</span>
            <input
              id="sf-legalName"
              type="text"
              className="sf-input"
              value={input.legalName}
              onChange={(e) => update('legalName', e.target.value)}
              placeholder="Juan Pérez Reformas SL"
              data-testid="settings-fiscal-legal-name"
              aria-invalid={Boolean(errors.legalName)}
            />
            {errors.legalName && (
              <span className="sf-error" data-testid="settings-fiscal-legal-name-error">{errors.legalName}</span>
            )}
          </label>
        </div>

        <label className="sf-field" htmlFor="sf-address">
          <span className="sf-label">Dirección *</span>
          <input
            id="sf-address"
            type="text"
            className="sf-input"
            value={input.address}
            onChange={(e) => update('address', e.target.value)}
            placeholder="Calle Real 12, 3.º A"
            data-testid="settings-fiscal-address"
            aria-invalid={Boolean(errors.address)}
          />
          {errors.address && <span className="sf-error" data-testid="settings-fiscal-address-error">{errors.address}</span>}
        </label>

        <div className="sf-row sf-row--triple">
          <label className="sf-field" htmlFor="sf-postal">
            <span className="sf-label">CP *</span>
            <input
              id="sf-postal"
              type="text"
              className="sf-input"
              value={input.postalCode}
              onChange={(e) => update('postalCode', e.target.value)}
              placeholder="28001"
              maxLength={5}
              inputMode="numeric"
              data-testid="settings-fiscal-postal"
              aria-invalid={Boolean(errors.postalCode)}
            />
            {errors.postalCode && (
              <span className="sf-error" data-testid="settings-fiscal-postal-error">{errors.postalCode}</span>
            )}
          </label>
          <label className="sf-field" htmlFor="sf-city">
            <span className="sf-label">Localidad *</span>
            <input
              id="sf-city"
              type="text"
              className="sf-input"
              value={input.city}
              onChange={(e) => update('city', e.target.value)}
              placeholder="Madrid"
              data-testid="settings-fiscal-city"
              aria-invalid={Boolean(errors.city)}
            />
            {errors.city && <span className="sf-error" data-testid="settings-fiscal-city-error">{errors.city}</span>}
          </label>
          <label className="sf-field" htmlFor="sf-province">
            <span className="sf-label">Provincia *</span>
            <input
              id="sf-province"
              type="text"
              className="sf-input"
              value={input.province}
              onChange={(e) => update('province', e.target.value)}
              placeholder="Madrid"
              data-testid="settings-fiscal-province"
              aria-invalid={Boolean(errors.province)}
            />
            {errors.province && (
              <span className="sf-error" data-testid="settings-fiscal-province-error">{errors.province}</span>
            )}
          </label>
        </div>

        <label className="sf-field" htmlFor="sf-iban">
          <span className="sf-label">IBAN (opcional)</span>
          <input
            id="sf-iban"
            type="text"
            className="sf-input"
            value={input.iban ?? ''}
            onChange={(e) => update('iban', e.target.value)}
            placeholder="ES91 2100 0418 4502 0005 1332"
            spellCheck={false}
            autoComplete="off"
            data-testid="settings-fiscal-iban"
            aria-invalid={Boolean(errors.iban)}
          />
          {errors.iban && <span className="sf-error" data-testid="settings-fiscal-iban-error">{errors.iban}</span>}
        </label>

        <div className="sf-callout" data-testid="settings-fiscal-aeat-notice">
          <ShieldCheck size={20} strokeWidth={2} aria-hidden="true" />
          <span>
            Estos datos se envían cifrados al relay. El servidor los pasa a la
            AEAT sin almacenar copia en claro.
          </span>
        </div>

        {submitError && (
          <p className="sf-submit-error" data-testid="settings-fiscal-submit-error">
            {submitError}
          </p>
        )}

        <div className="sf-actions">
          <button
            type="submit"
            className="sf-submit"
            disabled={hasErrors || submitBusy}
            data-testid="settings-fiscal-submit"
          >
            {saved ? (
              <Check size={16} strokeWidth={2} aria-hidden="true" />
            ) : (
              <Save size={16} strokeWidth={2} aria-hidden="true" />
            )}
            {submitBusy ? 'Guardando…' : saved ? 'Guardado' : existing ? 'Guardar cambios' : 'Crear perfil fiscal'}
          </button>
          <button
            type="button"
            className="sf-cancel"
            onClick={() => navigate('/settings')}
            data-testid="settings-fiscal-cancel"
          >
            Cancelar
          </button>
        </div>
      </form>
    </main>
  );
}
